import { createAsyncThunk } from '@reduxjs/toolkit';
import { keycloakInit, UserDetailResponseDto, UserRequestDto } from 'shared/api';
import { userApi } from '../api/userApi';
import { InitialUserStateType } from './types';

// export const getUserByKeycloakId = createAsyncThunk<UserDetailResponseDto, string>(
//   'user/getByKeycloakId',
//   async (id: string, { rejectWithValue }) => {
//     try {
//       const response = await userApi.findUserByKeycloakUserId(id);
//       return response;
//     } catch (error) {
//       console.log('user/getByKeycloakId rejected:', error);
//       return rejectWithValue(error);
//     }
//   },
// );

export const getUserByKeycloakId = async (id: string): Promise<
{ user: UserDetailResponseDto | null, authorizationStatus: UserDetailResponseDto.status }> => {
  try {
    const response = await userApi.findUserByKeycloakUserId(id);
    return { user: response, authorizationStatus: response.status };
  } catch (error) {
    if (error.status === 404) {
      return { user: null, authorizationStatus: UserDetailResponseDto.status.INACTIVE };
    }
    return { user: null, authorizationStatus: UserDetailResponseDto.status.SUSPENDED };
  }
};

export const userInit = createAsyncThunk<InitialUserStateType, void, { rejectValue: InitialUserStateType }>(
  'user/init',
  async (_, { rejectWithValue }) => {
    try {
      const keycloakUser = await keycloakInit();
      const roles = keycloakUser.tokenParsed?.realm_access?.roles || [];
      const keycloakPhone = keycloakUser.tokenParsed?.phone_number || '';
      const userInfo = await getUserByKeycloakId(keycloakUser.tokenParsed?.sub);
      const existingUser = userInfo.user;

      // Досинхронизируем телефон из Keycloak только когда есть кого и чем обновлять.
      // Пользователя может ещё не быть в БД (статус INACTIVE) - тогда обновлять нечего,
      // он попадёт на форму регистрации и заполнит данные сам.
      // Пустые опциональные поля отправляем как undefined: на бэке mobilePhone и innCompany
      // проверяются по формату, и пустая строка их валидацию не проходит.
      if (existingUser?.id && !existingUser.mobilePhone && keycloakPhone) {
        try {
          await userApi.updateUser(existingUser.id, {
            mobilePhone: keycloakPhone,
            company: existingUser.company,
            email: existingUser.email,
            firstname: existingUser.firstname,
            lastname: existingUser.lastname,
            keycloakUserId: existingUser.keycloakUserId,
            status: existingUser.status || UserDetailResponseDto.status.ACTIVE,
            comment: existingUser.comment || undefined,
            middleName: existingUser.middleName || undefined,
            innCompany: existingUser.innCompany || undefined,
          });
        } catch (error) {
          // Синхронизация телефона не критична: вход из-за неё блокировать нельзя.
          // Телефон подтянется при следующем входе или через профиль.
          console.error('Не удалось синхронизировать телефон из Keycloak:', error);
        }
      }

      return {
        user: existingUser && {
          ...existingUser,
          mobilePhone: existingUser.mobilePhone || keycloakPhone,
        },
        authorizationStatus: userInfo.authorizationStatus,
        roles,
      };
    } catch (error) {
      return rejectWithValue({ user: null, authorizationStatus: UserDetailResponseDto.status.SUSPENDED, roles: [] });
    }
  },
);

export const getUserById = createAsyncThunk(
  'user/getById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await userApi.getUserById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateUser = createAsyncThunk(
  'user/update',
  async ({ id, data }: { id: number; data: UserRequestDto }, { rejectWithValue }) => {
    try {
      const response = await userApi.updateUser(id, data);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const createUser = createAsyncThunk(
  'user/create',
  async (data: UserRequestDto, { rejectWithValue }) => {
    try {
      const response = await userApi.createUser(data);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);
